<template>
  <main class="view review">
    <div class="meta">
      <router-link to="/inquiries">
        <Icon name="chevronLeft" />
        <span>Fallübersicht</span>
      </router-link>
      <div class="caseinfo">
        <Icon :name="computedCaseinfo.icon" />
        <span class="name">{{ computedCaseinfo.name }}</span>
        <span class="birthdate">{{  computedCaseinfo.date }}</span>
      </div>
      <Dropdown
        secondary
        placeholder="Bearbeiten"
        ref="actionDropdown"
        :action-only="true"
        :static-placeholder="true"
        :options="computedInquiryActions"
        @input="handleInquiryAction"

      />
    </div>
    <div class="progress">
      <InquiryProgressExtended
        :status="inquiryStatus"
      />
    </div>
    <div class="container" ref="formContainer">
      <InteractionBar
        :actions="interactions"
        @interaction="handleInteraction"
      />
      <div class="report-container">
        <div>
          <ul>
            <li
              v-for="section in computedSections"
              :key="`section-${section.index}`"
              :class="{ active: section.index === activeSection }"
              @click="setActiveSection(section.index)">
                <span>{{ section.label }}</span>
              <SectionChangesIndicator :changes="section.openChanges" />
            </li>
          </ul>
        </div>
        <div :class="{ readonly: readOnly }">
          <h1 v-if="computedSectionHeading">
            {{ computedSectionHeading }}
            <Tooltip
              v-if="computedSectionTooltip"
              icon="info"
              :content="computedSectionTooltip"
            />
          </h1>
          <template v-if="computedSectionGroups.length > 0">
            <div
              class="group"
              v-for="(group, groupIndex) in computedSectionGroups"
              :key="`section-${activeSection}-group-${groupIndex}`">
                <h3 v-if="group.label || group.tooltip">
                  {{ group.label }}
                  <Icon v-if="group.forState" name="fieldsState" />
                  <Tooltip v-if="group.tooltip" icon="info" :content="group.tooltip"/>
                </h3>
                <template v-for="(comp, compIndex) in group.children">
                  <div
                    class="input"
                    :class="{ diff: resolved_values[comp.id] !== true && field_values[comp.id] }"
                    :key="comp.id">
                    <Select
                      fill
                      v-if="comp.component === 'select'"
                      :label="comp.props.label"
                      :hint="comp.props.hint"
                      :tooltip="comp.props.tooltip"
                      :options="comp.props.options"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="field_values[comp.id]"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <SelectEnhanced
                      fill
                      v-else-if="comp.component === 'select-enhanced'"
                      :key="`section-${activeSection}-group-${groupIndex}-comp-${compIndex}`"
                      :label="comp.props.label"
                      :hint="comp.props.hint"
                      :tooltip="comp.props.tooltip"
                      :options="comp.props.options"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="fieldvalues[comp.id]"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <TextInput
                      fill
                      v-else-if="comp.component === 'textinput'"
                      :label="comp.props.label"
                      :hint="comp.props.hint"
                      :placeholder="comp.props.placeholder"
                      :tooltip="comp.props.tooltip"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="field_values[comp.id]"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <TextareaInput
                      fill
                      v-else-if="comp.component === 'textarea'"
                      :label="comp.props.label"
                      :hint="comp.props.hint"
                      :tooltip="comp.props.tooltip"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="field_values[comp.id]"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <RadioGroup
                      review-mode
                      v-else-if="comp.component === 'radio-group'"
                      :label="comp.props.label"
                      :tooltip="comp.props.tooltip"
                      :options="comp.props.options"
                      :classes="comp.props.classes"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="field_values[comp.id]"
                      :changed.sync="changed_values[comp.id]"
                      @adopt="handleValueAdoption(comp.id, $event)"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <CheckboxGroup
                      :review-mode="resolved_values[comp.id] !== true"
                      v-else-if="comp.component === 'checkbox-group'"
                      :label="comp.props.label"
                      :tooltip="comp.props.tooltip"
                      :dynamic="comp.props.dynamic"
                      :dynamic-label="comp.props.dynamicLabel"
                      :options="comp.props.options"
                      :classes="comp.props.classes"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="field_values[comp.id]"
                      :changes.sync="changed_values[comp.id]"
                      :deleted.sync="deleted_values[comp.id]"
                      @adopt="handleValueAdoption(comp.id, $event)"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <RadioMatrix
                      v-else-if="comp.component === 'radio-matrix'"
                      :choices="comp.props.choices"
                      :dynamic="comp.props.dynamic"
                      :dynamic-label="comp.props.dynamicLabel"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="field_values[comp.id]"
                      :review-mode="resolved_values[comp.id] !== true"
                      :review-id="comp.id"
                      :review-data="storedReviewData"
                      @adopt="handleValueAdoption(comp.id, $event)"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <Datepicker
                      v-else-if="comp.component === 'datepicker'"
                      :label="comp.props.label"
                      :hint="comp.props.hint"
                      :tooltip="comp.props.tooltip"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :minus-years="(comp.props || {}).minusYears || 0"
                      :value.sync="field_values[comp.id]"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <RadioMatrixAdvanced
                      v-else-if="comp.component === 'radio-matrix-advanced'"
                      :choices="comp.props.choices"
                      :dynamic="comp.props.dynamic"
                      :dynamic-label="comp.props.dynamicLabel"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="field_values[comp.id]"
                      :review-mode="resolved_values[comp.id] !== true"
                      :review-id="comp.id"
                      :review-data="storedReviewData"
                      @adopt="handleValueAdoption(comp.id, $event)"
                      @input="handleValueInput(comp.id, $event)"
                    />
                    <DiagnosisInput
                      v-else-if="comp.component === 'diagnosis'"
                      :types="comp.props.types"
                      :diagnoses="comp.props.diagnoses"
                      :codes="comp.props.codes"
                      :dynamic="comp.props.dynamic"
                      :dynamic-label="comp.props.dynamicLabel"
                      :for-state="comp.for_state"
                      :for-report="comp.for_report"
                      :value.sync="field_values[comp.id]"
                      :review-mode="resolved_values[comp.id] !== true"
                      :review-id="comp.id"
                      :review-data="storedReviewData"
                      @adopt="handleValueAdoption(comp.id, $event)"
                      @input="handleValueInput(comp.id, $event)"
                      :data-version="2"
                    />

                    <!-- Difference -->
                    <Select
                      fill
                      difference
                      v-if="comp.component === 'select' && resolved_values[comp.id] !== true"
                      :label="$t('review.diff.label')"
                      :options="comp.props.options"
                      :value.sync="changed_values[comp.id]"
                      @adopt="handleValueAdoption(comp.id, $event)"
                    />
                    <TextInput
                      fill
                      difference
                      v-else-if="comp.component === 'textinput' && resolved_values[comp.id] !== true"
                      :label="$t('review.diff.label')"
                      :hint="comp.props.hint"
                      :value.sync="changed_values[comp.id]"
                      @adopt="handleValueAdoption(comp.id, $event)"
                    />
                    <TextareaInput
                      fill
                      difference
                      v-else-if="comp.component === 'textarea' && resolved_values[comp.id] !== true"
                      :label="$t('review.diff.label')"
                      :value.sync="changed_values[comp.id]"
                      @adopt="handleValueAdoption(comp.id, $event)"
                    />
                    <Datepicker
                      difference
                      v-else-if="comp.component === 'datepicker' && resolved_values[comp.id] !== true"
                      :label="$t('review.diff.label')"
                      :value.sync="changed_values[comp.id]"
                      @adopt="handleValueAdoption(comp.id, $event)"
                    />

                    <Spacer v-if="comp.component === 'spacer'" />

                    <div v-if="(comments[comp.id] && resolved_values[comp.id] !== true) || (comments[comp.id] && toggleComments)" class="comment">
                      <Icon name="comment" />
                      <blockquote :inner-html.prop="comments[comp.id] | linebreaks" />
                    </div>
                  </div>
                </template>
            </div>
            <div class="section-actions">
              <Button
                :disabled="!canNavigateBack"
                @click="prevSection">
                  Vorherige Seite
              </Button>
              <Button
                :disabled="!canNavigateForward"
                @click="nextSection">
                  Nächste Seite
              </Button>
            </div>
          </template>
        </div>
      </div>
      <div class="case-actions">
        <Button
          v-for="(action, idx) in computedNavActions"
          :key="`selectiveAction-${idx}`"
          :secondary="!action.primary"
          :primary="action.primary === true"
          @click="handleSelectiveAction(action.value)"
        >
          {{ action.label }}
        </Button>
      </div>
    </div>
  </main>
</template>

<script>
  import _ from 'lodash';
  import { updatedDiff, diff } from 'deep-object-diff';
  import InteractionBar from '@/components/ui/inputs/InteractionBar';
  import ToggleBar from '@/components/ui/inputs/ToggleBar';
  import RadioGroup from '@/components/ui/inputs/groups/RadioGroup';
  import TextareaInput from '@/components/ui/inputs/TextareaInput';
  import RadioMatrix from '@/components/ui/inputs/RadioMatrix';
  import DiagnosisInput from '@/components/ui/inputs/DiagnosisInput';
  import RadioMatrixAdvanced from '@/components/ui/inputs/RadioMatrixAdvanced';
  import TextInput from '@/components/ui/inputs/TextInput';
  import Select from '@/components/ui/inputs/Select';
  import SelectEnhanced from '@/components/ui/inputs/SelectEnhanced';
  import CheckboxGroup from '@/components/ui/inputs/groups/CheckboxGroup';
  import Datepicker from '@/components/ui/inputs/Datepicker';
  import Button from '@/components/ui/inputs/Button';
  import Icon from '@/components/ui/Icon';
  import Dropdown from '@/components/ui/inputs/Dropdown';
  import InquiryProgressExtended from '@/components/inquiry/InquiryProgressExtended';
  import Tooltip from '@/components/ui/Tooltip';
  import UndoRedoSave from '@/components/inquiry/UndoRedoSave';
  import InquiryStatus from '@/objects/InquiryStatus';
  import InquiryActions from '@/objects/InquiryActions';
  import SectionChangesIndicator from '@/components/review/SectionChangesIndicator';
  import ModalInquiryReview from '@/components/modals/ModalInquiryReview';
  import ModalInquiryReturn from '@/components/modals/ModalInquiryReturn';
  import ModalInquiryClosed from '@/components/modals/ModalInquiryClosed';
  import ModalInquiryDelete from '@/components/modals/ModalInquiryDelete';
  import ModalInquiryAnonymize from '@/components/modals/ModalInquiryAnonymize';
  import ModalInquiryArchive from '@/components/modals/ModalInquiryArchive';
  import ModalInquiryTransfer from '@/components/modals/ModalInquiryTransfer';
  import ModalInquiryDownload from '@/components/modals/ModalInquiryDownload';
  import ModalInquiryConflict from '@/components/modals/ModalInquiryConflict';
  import Spacer from '@/components/ui/Spacer';
  import { Store } from '@/store';
  import Crypto from '@/utils/Crypto';
  import DBStorage from '@/utils/DBStorage';
  import VueUtil from '@/utils/VueUtil';

  //----------------------------------
  // Constants
  //----------------------------------
  const REVIEW_ALL = 'all';
  const REVIEW_CHANGES = 'changes';

  export default {
    name: 'Review',
    components: {
      Spacer,
      SectionChangesIndicator,
      Tooltip,
      InquiryProgressExtended,
      Dropdown,
      Icon,
      Button,
      Datepicker,
      CheckboxGroup,
      Select,
      SelectEnhanced,
      TextInput,
      RadioMatrixAdvanced,
      DiagnosisInput,
      RadioMatrix,
      TextareaInput,
      RadioGroup,
      InteractionBar,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      uuid: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        inquiryid: null,
        inquiryStatus: InquiryStatus.OPEN,
        version: 2,

        fielddata: null,
        field_values: {},
        changed_values: {},
        deleted_values: {},
        resolved_values: {},

        storedReviewData: null,
        comments: {},
        toggleComments: false,

        saveStatus: null,
        saveTime: null,
        time: null,

        activeSection: 0,
        activeFilter: REVIEW_ALL,

        readOnly: false,

        openChanges: 0,

        preventFieldValueChange: false,
        saveTimeout: null,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      interactions() {
        const labels = this.$i18n.t('review.filters');
        return [
          {
            id: 'field-filter',
            type: ToggleBar,
            props: {
              secondary: true,
              value: REVIEW_ALL,
              options: [
                { value: REVIEW_ALL, label: labels.all, icon: 'fieldsAll' },
                { value: REVIEW_CHANGES, label: labels.changes, icon: 'fieldsAll' },
              ],
            },
          },
          {
            id: 'undoredosave',
            type: UndoRedoSave,
            props: {
              status: this.saveStatus,
              time: this.saveTime,
              noActions: true,
              showComments: true,
            },
          },
        ];
      },
      computedSections() {
        const fielddata = this.fielddata;
        if (fielddata) {
          const sections = [];
          const filter = this.activeFilter;
          fielddata.forEach((item) => {
            if (item.type === 'section') {
              if (filter === REVIEW_ALL || (filter === REVIEW_CHANGES && item.openChanges !== undefined)) {
                sections.push({
                  index: item.index,
                  label: item.label,
                  openChanges: item.openChanges,
                });
              }
            }
          });
          return sections;
        }
        return [];
      },
      computedSectionHeading() {
        const section = (this.fielddata || []).find(s => s.index === this.activeSection) || null;
        return (section?.heading) ? section.heading : null;
      },
      computedSectionTooltip() {
        const section = (this.fielddata || []).find(s => s.index === this.activeSection) || null;
        return (section?.tooltip) ? section.tooltip : null;
      },
      computedSectionGroups() {
        const section = (this.fielddata || []).find(s => s.index === this.activeSection) || {};
        if (section?.children) {
          const resolved = this.resolved_values;
          const out = _.cloneDeep(section.children);
          const filter = this.activeFilter;

          /** TODO: this was added in order to "hide" fields that are not needed for state or SAV ZH Report */
          out.forEach((group) => {
            // eslint-disable-next-line no-param-reassign
            group.children = group.children.filter(comp => comp.for_report || comp.for_state);
          });

          if (filter !== REVIEW_ALL) {
            out.forEach((group) => {
              // eslint-disable-next-line no-param-reassign
              group.children = group.children.filter(comp => filter === REVIEW_CHANGES && resolved[comp.id] !== true);
            });
          }
          return out;
        }
        return [];
      },
      computedCaseinfo() {
        const reqs = this.$i18n.t('req');
        const values = this.field_values;
        const f = values[reqs.f.v] || null;
        const l = values[reqs.l.v] || null;
        const d = values[reqs.d.v] || null;
        const g = values[reqs.g.v] || null;
        const out = {
          name: 'Unbekannt',
          date: '??.??.????',
          icon: 'anonymous',
        };
        if (f && l) {
          out.name = `${f} ${l}`;
        }
        if (d) {
          const [year, month, day] = d.split('-');
          out.date = `${day}.${month}.${year}`;
        }
        if (g === 'm') {
          out.icon = 'boy';
        } else if (g === 'w') {
          out.icon = 'girl';
        }
        return out;
      },
      computedInquiryActions() {
        const actions = InquiryActions.byStatus(this.inquiryStatus);
        return actions.filter(a => a.disabled === false).map(a => ({ ...a, value: a.action }));
      },
      computedNavActions() {
        const actions = this.$i18n.t('inquiry.navAction') || [];
        actions[actions.length - 1].primary = true;
        return actions;
      },
      canNavigateForward() {
        const sections = this.computedSections;
        const foundIndex = sections.findIndex(s => s.index === this.activeSection);
        const nextSection = sections[foundIndex + 1] || null;
        return nextSection !== null;
      },
      canNavigateBack() {
        const sections = this.computedSections;
        const foundIndex = sections.findIndex(s => s.index === this.activeSection);
        const prevSection = sections[foundIndex - 1] || null;
        return prevSection !== null;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      activeSection() {
        this.$nextTick(() => {
          const { formContainer } = this.$refs;
          const rect = formContainer.getBoundingClientRect();
          const scrollTop = document.documentElement.scrollTop;
          window.scrollTo({ left: 0, top: (rect.top + scrollTop), behavior: 'smooth' });
        });
      },
      computedSections(sections) {
        let index = this.activeSection;
        let found = false;
        let newIndex = null;
        while (!found) {
          // eslint-disable-next-line no-loop-func
          const section = sections.find(s => s.index === index);
          if (section) {
            newIndex = section.index;
            found = true;
          } else if (index > 0) {
            index -= 1;
          } else {
            newIndex = 0;
            found = true;
          }
        }
        this.activeSection = newIndex;
      },
      field_values: {
        async handler() {
          this.updateSectionsProgress();
          if (!this.preventFieldValueChange) {
            clearTimeout(this.saveTimeout);
            await this.storeToIndexedDB();
            this.saveTimeout = setTimeout(() => { this.saveData(); }, 1750);
          }
        },
        deep: true,
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {
      linebreaks(value) {
        return (value || '').replace(/\n/g, '<br>');
      },
    },
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    async created() {
      const inquiryid = this.uuid;
      if (inquiryid) {
        const inquiry = await this.$sav.api.get(`/inquiry/${inquiryid}`);
        this.inquiryid = inquiryid;
        this.inquiryStatus = inquiry.status;
        this.version = inquiry?.version || this.version;
        this.saveTime = new Date(inquiry.modified);
        this.saveStatus = 'idle';

        const localData = await DBStorage.get(this.inquiryid);
        const localVersion = ((localData || {}).versions || [])[0] || null;
        if (localVersion && localVersion.hash !== inquiry.hash) {
          console.log('Discrepancy between local and cloud data');
          this.resolveDataConflict(localData, inquiry, true);
        } else {
          this.populateData(inquiry);
        }
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // UI Methods
      //----------------------------------
      setActiveSection(idx) {
        this.activeSection = idx;
      },
      nextSection() {
        const sections = this.computedSections;
        const foundIndex = sections.findIndex(s => s.index === this.activeSection);
        const nextSection = sections[foundIndex + 1] || null;
        if (nextSection) {
          this.setActiveSection(nextSection.index);
        }
      },
      prevSection() {
        const sections = this.computedSections;
        const foundIndex = sections.findIndex(s => s.index === this.activeSection);
        const prevSection = sections[foundIndex - 1] || null;
        if (prevSection) {
          this.setActiveSection(prevSection.index);
        }
      },
      backToList() {
        this.$router.back();
      },
      async executeAction(action) {
        await this.storeToIndexedDB();
        await this.saveData();
        switch (action) {
          case InquiryActions.REVIEW: {
            const modal = VueUtil.createComponent(ModalInquiryReview, document.body, {
              propsData: {
                uuid: this.uuid,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.RETURN_REVIEW: {
            const modal = VueUtil.createComponent(ModalInquiryReturn, document.body, {
              propsData: {
                uuid: this.uuid,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.CLOSE: {
            const modal = VueUtil.createComponent(ModalInquiryClosed, document.body, {
              propsData: {
                uuid: this.uuid,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.ANONYMIZE: {
            const modal = VueUtil.createComponent(ModalInquiryAnonymize, document.body, {
              propsData: {
                uuid: this.uuid,
                status: this.inquiryStatus,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.ARCHIVE: {
            const modal = VueUtil.createComponent(ModalInquiryArchive, document.body, {
              propsData: {
                uuid: this.uuid,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.TRANSFER: {
            const modal = VueUtil.createComponent(ModalInquiryTransfer, document.body, {
              propsData: {
                uuid: this.uuid,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.DOWNLOAD: {
            VueUtil.createComponent(ModalInquiryDownload, document.body, {
              propsData: {
                uuid: this.uuid,
                savId: this.inquiryid,
                structure: this.fielddata,
                values: this.field_values,
              },
              parent: this,
            });
            break;
          }
          case InquiryActions.DELETE: {
            const modal = VueUtil.createComponent(ModalInquiryDelete, document.body, {
              propsData: {
                uuid: this.uuid,
              },
              parent: this,
            });
            modal.$once('done', async () => { this.backToList(); });
            break;
          }
          case InquiryActions.SAVECLOSE: {
            this.backToList();
            break;
          }
          default: {
            console.log(action);
          }
        }
      },

      //----------------------------------
      // Data Methods
      //----------------------------------
      async populateData(inquiry) {
        const { data } = await this.constructData();

        const reviewData = inquiry.reviewdata?.data || inquiry.reviewdata;
        this.storedReviewData = reviewData;
        this.comments = inquiry.reviewdata?.comments || {};

        this.preventFieldValueChange = true;

        this.field_values = inquiry.data;

        /** TODO: this was added in order to "hide" fields that are not needed for state or SAV ZH Report */
        const relevantIds = [];
        data.forEach((section) => {
          section.children.forEach((group) => {
            group.children.forEach((comp) => {
              if (comp.for_report || comp.for_state) {
                relevantIds.push(comp.id);
              }
            });
          });
        });

        const relevantInquiryData = {};
        relevantIds.forEach((id) => {
          relevantInquiryData[id] = inquiry.data[id];
        });
        const relevantReviewData = {};
        relevantIds.forEach((id) => {
          relevantReviewData[id] = reviewData[id];
        });

        this.changed_values = updatedDiff(relevantInquiryData, relevantReviewData);
        this.deleted_values = diff(relevantReviewData, relevantInquiryData);
        /** END OF CHANGE */

        /** OLD diff */
        // this.changed_values = updatedDiff(inquiry.data, reviewData);
        // this.deleted_values = diff(reviewData, inquiry.data);

        Object.keys(inquiry.data).forEach((key) => {
          this.resolved_values[key] = !(this.changed_values[key] || this.deleted_values[key]);
        });

        this.fielddata = data;
        this.updateSectionsProgress();

        this.$nextTick(() => { this.preventFieldValueChange = false; });
      },

      async constructData(stubOnly = false) {
        const fielddata = this.$i18n.t(`fielddata_versions.${this.version}`);
        const values = {};

        let data = fielddata.filter(section => !stubOnly || (stubOnly && section.stub));
        data = data.map((section) => {
          // eslint-disable-next-line no-param-reassign
          section.children = section.children.filter((group) => {
            // eslint-disable-next-line no-param-reassign
            group.children = group.children.filter(comp => !stubOnly || (stubOnly && comp.stub));
            return group.children.length > 0;
          });
          return section;
        });

        for (let s = 0, slen = data.length; s < slen; s += 1) {
          const section = data[s];
          section.index = s;
          const sectionChildren = section?.children || [];

          for (let c = 0, clen = sectionChildren.length; c < clen; c += 1) {
            const group = sectionChildren[c];
            const groupChildren = group?.children || [];

            for (let g = 0, glen = groupChildren.length; g < glen; g += 1) {
              const comp = groupChildren[g];
              if (comp.type === 'input') {
                values[comp.id] = comp?.props.value || null;
                if (comp?.props.valueFunc) {
                  const fn = this[comp.props.valueFunc];
                  // eslint-disable-next-line no-await-in-loop
                  values[comp.id] = await fn() || null;
                }

                if (comp?.props.optionsFunc) {
                  const fn = this[comp.props.optionsFunc];
                  if (fn) {
                    // eslint-disable-next-line no-await-in-loop
                    const { options, value } = await fn() || null;
                    if (options) {
                      comp.props.options = options;
                    }
                    if (value) {
                      values[comp.id] = value;
                    }
                  }
                }
              }
            }
          }
        }
        return { data, values };
      },

      async saveData() {
        this.saveStatus = 'updating';
        const values = this.cloneObject(this.field_values);
        const reviewData = this.cloneObject(this.storedReviewData);

        const postdata = {
          uuid: this.inquiryid,
          status: this.inquiryStatus,
          data: values,
          reviewdata: reviewData,
          comments: this.comments,
        };

        const response = await this.$sav.api.post('/inquiry', null, postdata);
        if (response?.uuid) {
          this.inquiryStatus = response.status;
          this.saveStatus = 'updated';
          this.saveTime = new Date(response.modified);
        }
      },

      //----------------------------------
      // Data Conflict Methods
      //----------------------------------
      resolveDataConflict(localData, inquiry, warn = true) {
        this.readOnly = true;
        const latestRemoteVersion = {
          data: inquiry.data,
          modified: Math.floor(new Date(inquiry.modified).getTime() / 1000),
          local: false,
        };
        const remoteVers = (inquiry.versions || []).map(v => ({ ...v, local: false }));
        remoteVers.unshift(latestRemoteVersion);
        const localVers = (localData.versions || []).map(v => ({ ...v, local: true }));
        const versions = [...remoteVers, ...localVers].sort((a, b) => b.modified - a.modified);

        const modal = VueUtil.createComponent(ModalInquiryConflict, document.body, { propsData: { warn, versions }, parent: this });
        modal.$once('resolve', (resp) => {
          const inq = this.cloneObject(inquiry);
          this.$nextTick(async () => {
            const { data, local } = resp;
            inq.data = !local ? data : await Crypto.decrypt(data, Store.skey);
            this.readOnly = false;
            this.populateData(inq);
          });
        });
        modal.$once('cancel', () => {
          this.populateData(inquiry);
        });
        modal.$on('change', async (resp) => {
          const inq = this.cloneObject(inquiry);
          this.$nextTick(async () => {
            const { data, local } = resp;
            inq.data = !local ? data : await Crypto.decrypt(data, Store.skey);
            this.populateData(inq);
          });
        });
      },

      //----------------------------------
      // IndexedDB Methods
      //----------------------------------
      async storeToIndexedDB() {
        const uuid = this.inquiryid;
        if (uuid) {
          const entry = await DBStorage.get(uuid) || {
            uuid,
            versions: [],
          };

          const values = this.cloneObject(this.field_values);
          const threshold = 300;
          const now = Math.floor(Date.now() / 1000);

          const newEntry = {
            data: await Crypto.encrypt(values, Store.skey),
            hash: await Crypto.generateHash(values),
            modified: now,
          };

          const lastVersion = entry.versions[0];
          const lastModified = lastVersion?.modified || 0;
          const hasChanged = lastVersion?.hash !== newEntry.hash;
          const shouldCreateNewVersion = (lastModified + threshold < now) && hasChanged;

          if (entry.versions.length === 0 || shouldCreateNewVersion) {
            entry.versions.unshift(newEntry);
          } else {
            entry.versions[0] = newEntry;
          }

          if (entry.versions.length > 10) {
            entry.versions = entry.versions.slice(0, 10);
          }

          await DBStorage.set(entry);
        }
      },

      //----------------------------------
      // Custom Options / Value  Methods
      //----------------------------------
      async getBranches() {
        const branch = Store.branch;
        const value = branch.uuid;
        const options = [{ value, label: branch.name }];
        return { options, value };
      },

      async getPsychologists() {
        const users = await this.$sav.api.get('/users/psychologists');
        const options = (users || []).map(user => ({ value: user.uuid, label: user.name }));
        return { options };
      },

      async getCurrentDate() {
        const date = new Date();
        return date.toISOString().split('T')[0];
      },

      async getUserUuid() {
        return Store.uuid;
      },

      //----------------------------------
      // Helper Methods
      //----------------------------------
      updateSectionsProgress() {
        const data = this.cloneObject(this.fielddata);
        const resolved = this.resolved_values || {};
        let openChanges = 0;

        data.forEach((section) => {
          const inputs = this.getAllSectionInputs(section);
          const unresolved = inputs.filter(input => resolved[input.id] !== true);

          // eslint-disable-next-line no-param-reassign
          section.openChanges = unresolved.length > 0;
          openChanges += (unresolved.length > 0) ? 1 : 0;
        });
        this.fielddata = data;
        this.openChanges = openChanges;
      },

      getAllSectionInputs(section) {
        const inputs = [];
        section.children.forEach((group) => {
          group.children.forEach((child) => {
            if (child.type === 'input') {
              inputs.push(child);
            }
          });
        });
        return inputs;
      },

      cloneObject(obj) {
        return JSON.parse(JSON.stringify(obj));
      },

      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleInteraction(data) {
        const { id, value } = data;
        if (id === 'field-filter') {
          this.activeFilter = value;
        }
        if (id === 'undoredosave' && value === 'toggle-comments') {
          this.toggleComments = !this.toggleComments;
        }
      },
      async handleValueAdoption(id, adopt) {
        this.preventFieldValueChange = true;
        const values = this.cloneObject(this.field_values);
        const reviewData = this.cloneObject(this.storedReviewData);
        const comments = this.cloneObject(this.comments);
        const changed_values = this.cloneObject(this.changed_values);
        const deleted_values = this.cloneObject(this.deleted_values);
        const resolved_values = this.cloneObject(this.resolved_values);

        const { accept, value } = adopt;
        if (accept) {
          values[id] = value;
          reviewData[id] = value;
          delete comments[id];
        }

        reviewData[id] = values[id];
        delete comments[id];
        resolved_values[id] = true;
        changed_values[id] = null;
        deleted_values[id] = null;

        this.storedReviewData = reviewData;
        this.comments = comments;
        this.resolved_values = resolved_values;
        this.changed_values = changed_values;
        this.deleted_values = deleted_values;
        this.field_values = values;
        await this.storeToIndexedDB();
        await this.saveData();
        this.$nextTick(() => {
          this.preventFieldValueChange = false;
        });
      },
      handleValueInput(id, value) {
        if (!this.preventFieldValueChange) {
          const resolved_values = this.cloneObject(this.resolved_values);
          if (resolved_values[id] === true) {
            const reviewData = this.cloneObject(this.storedReviewData);
            reviewData[id] = value;
            this.storedReviewData = reviewData;
          }
        }
      },
      handleInquiryAction(value) {
        if (value) {
          const { actionDropdown } = this.$refs;
          actionDropdown.close();
          this.executeAction(value);
        }
      },
      handleSelectiveAction(value) {
        this.executeAction(value);
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

main.view.review {
  padding-top: 32px;

  .meta {
    height: 48px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    a {
      color: $color-black-alpha-60;
      text-transform: uppercase;
      text-decoration: none;
      svg {
        position: relative;
        top: 2px;
        width: 16px;
        height: 16px;
        margin-right: 8px;
      }
    }

    .caseinfo {
      height: 48px;
      border-radius: $default-border-radius;
      background-color: $color-white-solid-10;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 12px 24px;

      & > svg {
        width: 24px;
        height: 24px;
      }

      & > .name {
        margin-left: 12px;
        margin-right: 8px;
        @include font-body-medium;
        color: $color-black-solid-100;
      }
      & > .birthdate {
        @include font-body-small;
        color: $color-black-alpha-60;
      }
    }
  }
  .progress {
    display: flex;
    justify-content: center;
    padding: 32px 0;
  }
  .container {
    position: relative;
    background-color: $color-white-solid-10;
    box-shadow: $box-shadow-2;
    min-height: 48px;
    border-radius: $default-border-radius;

    & > .comp-interactionbar {
      width: 100%;
      position: sticky;
      top: 0;
      z-index: 3;
      justify-content: space-between;
      background-color: $color-white-solid-10;
      border-bottom: 1px solid $color-black-alpha-20;
    }

    & > .report-container {
      display: flex;
      padding: 32px;

      & > div:first-of-type {
        margin-right: 64px;

        ul {
          position: sticky;
          top: 105px;
          width: 276px;
          list-style-type: none;
          padding: 0;
          margin: 0;

          li {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-radius: $default-border-radius;
            background-color: $color-blue-alpha-20;
            padding: 12px 11px;
            margin-bottom: 4px;
            @include font-body-medium;
            color: $color-black-solid-100;
            user-select: none;
            cursor: pointer;

            & > span { padding-right: 8px; }
            & > .comp-progress-circle { min-width: 24px; }

            &:last-of-type {
              margin-bottom: 0;
            }

            &.active {
              cursor: default;
              background-color: transparent;
              @include font-body-medium-bold;
            }
          }
        }
      }
      & > div:last-of-type {
        width: 100%;
        // max-width: 956px;
        h1 {
          margin-bottom: 56px;
          .comp-tooltip {
            position: relative;
            top: -2px;
            & > svg {
              width: 22px;
              height: 22px;
            }
          }
        }
        h3 {
          margin-bottom: 24px;
          & > svg {
            margin: 0 6px;
            display: inline-block;
            position: relative;
            top: 1px;
            max-width: 14px;
            max-height: 14px;
          }
        }

        .group {
          padding: 36px 0 24px 0;
          border-bottom: 1px solid $color-black-alpha-10;
          &:first-of-type {
            padding-top: 0;
          }
          &:last-of-type {
            padding-bottom: 0;
            border-bottom: 0;
          }

          & > div.input {
            position: relative;
            margin-bottom: 24px;

            &:after {
              content: '';
              position: absolute;
              left: 0;
              top: 50%;
              transform: translate(-20px, -50%);
              height: 0;
              opacity: 0;
              width: 3px;
              border-radius: 3px;
              background-color: $color-signal-green;
              transition: height $default-transition, opacity $default-transition;
            }

            &.diff:after {
              content: '';
              position: absolute;
              left: 0;
              top: 50%;
              transform: translate(-20px, -45%);
              height: 105%;
              width: 3px;
              border-radius: 3px;
              opacity: 1;
              background-color: $color-signal-red;
            }

            & > * {
              margin-bottom: 24px;
              &:last-of-type {
                margin-bottom: 0;
              }
            }

            & > .comment {
              display: inline-flex;
              justify-content: flex-start;
              align-items: center;
              gap: 16px;
              min-width: 33.33%;
              padding: 8px 16px 8px 16px;
              // background-color: $color-black-alpha-05;
              background: linear-gradient(90deg, $color-black-alpha-05 80%, $color-white-solid-10);

              & > svg {
                position: relative;
                top: 2px;
                width: 32px;
                height: 32px;
              }
              & > blockquote {
                min-width: 33.33%;
                margin: 0;
                padding: 0;
                font-style: italic;
                font-size: 1.3rem;
                color: $color-black-solid-100;
              }
            }
          }
        }

        .section-actions {
          display: flex;
          justify-content: center;
          padding: 38px 0 0;

          button {
            margin-right: 16px;
            &:last-of-type { margin-right: 0; }
          }
        }

        &.readonly {
          pointer-events: none;
        }
      }
    }

    & > .case-actions {
      display: flex;
      justify-content: flex-end;
      padding: 17px 20px;
      border-top: 1px solid $color-black-alpha-10;

      button {
        margin-right: 16px;
        &:last-of-type { margin-right: 0; }
      }
    }
  }
}
</style>
